import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Input } from "@mui/material";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Loader } from "../../helpers";
import MainContainer from "../../layout/MainContainer";
import "react-phone-input-2/lib/bootstrap.css";
import { CommonBody, UserById } from "../../types/General";
import { UploadMedia } from "../../utils/mediaUpload";
import { isNumber } from "../../utils/validations";
import { showError, showToast } from "../../helpers/showToast";
import { useLazyUserDetailsQuery, useUpdateUserMutation } from "service/user";
import { generateEncryptedKeyBody } from "utils/crypto";

const EditProfile = () => {
  const { id } = useParams();
  const [getDetail] = useLazyUserDetailsQuery();
  const [updateUser] = useUpdateUserMutation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [details, setUserDetails] = useState<UserById>();

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setProfileImage(imageUrl?.data?.image || "");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      firstName: details?.firstName || "",
      lastName: details?.lastName || "",
      address: details?.addressObject?.address || "",
      email: details?.email || "",
      dialCode: details?.countryCode || "",
      phone: details?.phone || "",
    },

    validationSchema: Yup.object({
      firstName: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 characters are required"),
      lastName: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 characters are required"),
      // address: Yup.string()
      //   .required("This field is required")
      //   .min(3, "Minimum 3 characters are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        // image: profileImage,
        firstName: values?.firstName || "",
        lastName: values.lastName || "",
        // address: values?.address || "",
      };

      if (id) {
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateUser({
            body: encryptedBody,
            id,
          }).unwrap();
          if (response?.statusCode === 200) {
            showToast("Profile updated successfully");

            navigate(-1);
          }
        } catch (error: any) {
          showError(error?.data?.message || "");
          console.error(error);
        }
      }
    },
  });

  const getUserDetails = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setUserDetails(response?.data);
        setProfileImage(response?.data?.image || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    formik.setFieldValue("dialCode", country?.dialCode);
  };

  useEffect(() => {
    if (id) {
      getUserDetails(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>Edit Profile Detail</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
            Registrations - Edit Profile Detail
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Profile Image</label>
              <div className="upload_box">
                <label>
                  {profileImage && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => setProfileImage("")}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={
                        profileImage
                          ? profileImage
                          : "/static/images/user_placeholder.png"
                      }
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(e: any) => {
                            handleProfileUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
            </div>

            <div className="control_group w_50" />
            <div className="control_group w_50">
              <label>First name</label>
              <TextField
                variant="outlined"
                placeholder="First name"
                className="text_field"
                fullWidth
                hiddenLabel
                name="firstName"
                id="firstName"
                inputProps={{ maxLength: 80 }}
                value={formik.values.firstName}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </div>

            <div className="control_group w_50">
              <label>Last name</label>
              <TextField
                variant="outlined"
                placeholder="Last name"
                className="text_field"
                fullWidth
                hiddenLabel
                name="lastName"
                id="lastName"
                inputProps={{ maxLength: 80 }}
                value={formik.values.lastName}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </div>

            <div className="control_group w_50">
              <label>E-mail</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                value={formik.values.email}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>
            <div className="control_group w_50">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={formik.values.dialCode + formik.values.phone}
                country={"us"}
                inputClass="phoneInput"
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
                onChange={(phone, country) => handleChangePhone(phone, country)}
                // className="text_field"
              />
            </div>

            {/* <div className="control_group w_50">
              <label> Address</label>
              <TextField
                variant="outlined"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                name="address"
                id="address"
                className="text_field"
                inputProps={{ maxLength: 200 }}
                value={formik.values.address}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.address && formik.errors.address}
              />
            </div> */}
          </div>
          <div className="form_btn end">
            <Button className="btn btn_secondary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default EditProfile;
